import ProjectWorld, { ProjectEventType, ProjectLocalEvent, ProjectMetadata, ProjectProjectionResult } from "@/ProjectWorld";
import { inject, injectable } from "tsyringe";
import { ErrorForbidden, ErrorNotExist, type IServerCalls, TOKEN_IServerCalls } from "./ServerCalls";
import log from 'loglevel';
import { type IRealtime, TOKEN_IRealtime } from "./Realtime";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "./ProjectWorldReader";
import { type IProjectWorldWriter, TOKEN_IProjectWorldWriter } from "./ProjectWorldWriter";
import { ThrottledSync } from '@utilitydelta/clientside-event-sourcing'
import { type IWrapTime, TOKEN_IWrapTime } from "./WrapTime";
import { type IProjectBus, ProjectBusEvents, ServerIssueType, TOKEN_IProjectBus } from "./ProjectBus";
import { type IUserIdentity, TOKEN_IUserIdentity } from "./UserIdentity";
import { neverEmptyString } from "@/lib/utils";
import { TOKEN_INavigationState, type INavigationState } from "./NavigationState";
import { TOKEN_IBroadcastChannel, type IBroadcastChannel } from "./BroadcastChannelService";
import { AccessLevel, ServerEvent, ServerEventBatch } from '@utilitydelta/eventplanedb-client';
import { ab2str, base64ToArrayBuffer, decryptAsymmetric, encryptedProject, generateShortClientIdentity, importRsaPrivateKeyForDecryption } from "@utilitydelta/nosignup-js";
import { TOKEN_ISecureServerCalls, type ISecureServerCalls } from "./SecureServerCalls";
import { TOKEN_IWrapAuth0, type IWrapAuth0 } from "./WrapAuth0";
import { ProvidedSymmetricKey_LocalEvent } from "@/events/ProvidedSymmetricKey";

export const TOKEN_IProjectSyncing = Symbol('IProjectSyncing');


export class EncryptedProjectError extends Error {
    constructor(message = 'Project is encrypted, requires symmetric key') {
        super(message);
        this.name = 'EncryptedProjectError';
    }
}

export class UnsupportedEventTypeError extends Error {
    constructor(message = 'Unsupported Data, App Requires Update') {
        super(message);
        this.name = 'UnsupportedEventTypeError';
    }
}

/*
 * Makes projects server-enabled. Handle initial load from server, syncs, 
 * and real-time connection via IRealtime. Any events going to the server 
 * are encrypted, and server events received are decrypted before processing.
 */
export interface IProjectSyncing {
    optInForServerIfLoggedIn(id: string): Promise<void>;

    undo(projectId: string): Promise<void>;

    redo(projectId: string): Promise<void>;

    addLocalEvent(projectId: string, event: ProjectLocalEvent): Promise<void>;

    processServerEvents(projectWorld: ProjectWorld, eventBatches: ServerEventBatch<ProjectEventType>[], pushOntoEventBus: boolean): Promise<ProjectLocalEvent[]>;

    readFromServer(projectId: string): Promise<void>;
    writeToServer(projectId: string): Promise<void>;
    intentionalOptInForServer(projectId: string): Promise<void>;
}

@injectable()
export class ProjectSyncing implements IProjectSyncing {

    private readonly SYNC_THROTTLE_MS = 50;
    private readonly _writeSync: ThrottledSync;
    private readonly _readSync: ThrottledSync;

    constructor(
        @inject(TOKEN_IProjectWorldReader) private projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IProjectWorldWriter) private projectWorldWriter: IProjectWorldWriter,
        @inject(TOKEN_IServerCalls) private serverCalls: IServerCalls,
        @inject(TOKEN_IRealtime) private realtime: IRealtime,
        @inject(TOKEN_IWrapTime) private wrapTime: IWrapTime,
        @inject(TOKEN_IUserIdentity) private userIdentity: IUserIdentity,
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus,
        @inject(TOKEN_INavigationState) private navigationState: INavigationState,
        @inject(TOKEN_IBroadcastChannel) private broadcastChannel: IBroadcastChannel,
        @inject(TOKEN_ISecureServerCalls) private secureServerCalls: ISecureServerCalls,
        @inject(TOKEN_IWrapAuth0) private wrapAuth0: IWrapAuth0,
    ) {
        this._writeSync = new ThrottledSync(this.writeToServer, this.SYNC_THROTTLE_MS);
        this._readSync = new ThrottledSync(this.readFromServer, this.SYNC_THROTTLE_MS);

        projectBus.subscribe_otherWindowLocalEvents(this.onOtherWindowLocalEvents);
    }

    optInForServerIfLoggedIn = async (projectId: string): Promise<void> => {
        const authData = await this.secureServerCalls.AuthData();
        if (authData.bearerToken === undefined) return;

        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        await this.optInForServerInternal(projectWorld);
    }

    private onOtherWindowLocalEvents = async (data: ProjectBusEvents['OTHER_WIND0W_LOCAL_EVENTS']) => {
        if (data.projectEvents.length === 0) return;

        const projectWorld = await this.projectWorldReader.mustGet(data.projectId);
        const mutations = projectWorld.processEventsIntoWorldWithoutUndo(data.projectEvents);
        this.notifyListeners(true, projectWorld.projectMetadata, mutations);
    }

    private async persistEvents(projectId: string, events: ProjectLocalEvent[], pushEventsOntoBus: boolean, isUndoEvent: boolean): Promise<void> {
        if (events.length === 0) return;

        const eventDate = this.wrapTime.getCurrentUnixTimestamp();
        const publicKey = await this.userIdentity.publicKey();
        const userId = this.wrapAuth0.userIdHashed;
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        const mutations = projectWorld.processEventsIntoWorld(events, isUndoEvent, publicKey.publicKeyHashed, userId, eventDate);
        const dirtyEntitiesCopy = projectWorld.copyAndClearDirtyEntities();

        await this.projectWorldWriter.saveProjectWorld(projectWorld.projectMetadata, events, dirtyEntitiesCopy);

        this.notifyListeners(pushEventsOntoBus, projectWorld.projectMetadata, mutations);

        //Tell other tabs that we have new events to update thier world model
        this.broadcastChannel.notifyProjectUpdate(projectId, events);
    };

    private notifyListeners(
        pushEventsOntoBus: boolean,
        projectMetadata: ProjectMetadata,
        mutations: ProjectProjectionResult[]
    ) {
        if (!pushEventsOntoBus) return;

        this.projectBus.projectMetadataUpdated(projectMetadata);
        mutations
            .filter(x => x !== undefined)
            .forEach(mutationResult => this.projectBus.projectionUpdated(projectMetadata.id, mutationResult));
    };

    processServerEvents = async (projectWorld: ProjectWorld, eventBatches: ServerEventBatch<ProjectEventType>[], pushOntoEventBus: boolean): Promise<ProjectLocalEvent[]> => {
        await this.optInForServerInternal(projectWorld);
        let localEvents: ProjectLocalEvent[] = [];
        try {
            localEvents = await this.decryptAndLoadServerEvents(projectWorld, eventBatches, pushOntoEventBus);
        } catch (e) {
            if (e instanceof EncryptedProjectError) {
                //Project syncing is not allowed to generate local events,
                //So just go realtime so we can wait for our symmetric key from creating client
                await this.maybeGoRealtime(projectWorld);
            }
            throw e;
        }
        await this.maybeGoRealtime(projectWorld);
        return localEvents;
    }


    intentionalOptInForServer = async (projectId: string) => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        if (projectWorld.projectMetadata.userOptInForServer) return;
        projectWorld.projectMetadata.userOptInForServer = true;
        await this.projectWorldWriter.saveProjectWorldMetadata(projectWorld.projectMetadata);
        await this.writeToServer(projectId);
    }

    private optInForServerInternal = async (projectWorld: ProjectWorld) => {
        if (projectWorld.projectMetadata.userOptInForServer) return;
        projectWorld.projectMetadata.userOptInForServer = true;
        await this.projectWorldWriter.saveProjectWorldMetadata(projectWorld.projectMetadata);
    }

    undo = async (projectId: string): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        const event = projectWorld.undoRedoStack.getEventToUndo();
        if (event === undefined) return;

        await this.persistEvents(projectId, [event], true, true);
        this._writeSync.syncNowOrWait(projectId);
    }

    redo = async (projectId: string): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        const event = projectWorld.undoRedoStack.getEventToRedo();
        if (event === undefined) return;

        await this.persistEvents(projectId, [event], true, true);
        this._writeSync.syncNowOrWait(projectId);
    }

    addLocalEvent = async (projectId: string, event: ProjectLocalEvent): Promise<void> => {
        await this.persistEvents(projectId, [event], true, false);
        this._writeSync.syncNowOrWait(projectId);
    }

    private async decryptAndLoadServerEvents(projectWorld: ProjectWorld, serverEventBatches: ServerEventBatch<ProjectEventType>[], pushOntoEventBus: boolean) {

        const localEvents = await Promise.all(serverEventBatches.map(async (serverEvents) => this.toLocalEvent(projectWorld, serverEvents)));
        const flatLocalEvents = localEvents.flat();

        //This will only happen if we can map all server events into local events without errors
        await this.persistEvents(
            projectWorld.projectMetadata.id,
            flatLocalEvents,
            pushOntoEventBus,
            false);

        return flatLocalEvents;
    }

    private async toLocalEvent(projectWorld: ProjectWorld, serverEventBatch: ServerEventBatch<ProjectEventType>): Promise<ProjectLocalEvent[]> {
        return await Promise.all(serverEventBatch.ev.map(async (serverEvent) => {

            if (serverEvent.iv !== undefined && projectWorld.encryptedProject !== undefined) {
                serverEvent.ev = await projectWorld.encryptedProject.decryptString(serverEvent.iv, serverEvent.ev);
            }

            if (serverEvent.iv !== undefined && projectWorld.encryptedProject === undefined) {
                //We can't decrypt so we can't process this event
                throw new EncryptedProjectError();
            }

            const processor = projectWorld.eventProcessors.getProcessor(serverEvent.tp);

            //We have an event from the server with an unknown event type. Can't process this event batch.
            if (processor === undefined) {
                throw new UnsupportedEventTypeError();
            }

            //We may miss critical data as this client doesn't understand the new version of this event type
            if (processor.eventTypeMinorVersion < serverEvent.tm) {
                throw new UnsupportedEventTypeError();
            }

            const localEvent = {
                //lEAVE CX OUT SO IT CAN BE AUTO GENERATED
                ex: serverEvent.ex,
                et: serverEvent.et,
                bx: serverEventBatch.bx,
                pi: projectWorld.projectMetadata.id,
                ci: serverEventBatch.ci,
                ui: serverEventBatch.ui,
                tp: serverEvent.tp,
                tm: serverEvent.tm,
            } as ProjectLocalEvent;

            await processor.fromServerEvent(localEvent, serverEvent);

            return localEvent;
        }));
    }

    private readEventsDueToRealtimeNotification = async (projectId: string) => {
        log.info(`Attempting to sync events with server due to realtime notification of new events`, projectId);
        await this._readSync.syncNowOrWait(projectId);
    }

    private maybeGoRealtime = async (project: ProjectWorld) => {
        const activeShareLinkCount = project.shareLinks.allNotDeleted.filter(x => x.isActive).length;
        const activeUserCount = project.projectUsers.allNotDeleted.filter(x => x.accessLevel !== AccessLevel.None).length;
        const isConnected = await this.realtime.isConnected(project.projectMetadata.id);

        if ((this.wrapAuth0.state.isAuthenticated || (activeUserCount > 1 || activeShareLinkCount > 0)) && !isConnected) {
            log.info('Project has multiple access entries, connecting websocket and going realtime', project.projectMetadata.id);
            await this.realtime.connect(project.projectMetadata.id, this.readEventsDueToRealtimeNotification);
        }

        if (this.wrapAuth0.state.isAuthenticated) return;

        if (activeUserCount <= 1 && activeShareLinkCount == 0 && isConnected) {
            log.info('Project not shared with multipe users, disconnecting websocket and going offline', project.projectMetadata.id);
            await this.realtime.disconnect(project.projectMetadata.id);
        }
    }

    readFromServer = async (projectId: string): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        try {
            await projectWorld.concurrentServerReading(this.readEventsFromServerAndDecrypt);
        } catch (error: unknown) {
            if (error instanceof ErrorForbidden) {
                log.error('Access forbidden to project.', projectId);
                await this.projectWorldWriter.deleteProjectWorld(projectId);
                this.projectBus.serverIssue(ServerIssueType.NoPermission);
                throw error;
            } else if (error instanceof ErrorNotExist) {
                log.error('Project not found on server', projectId);
                await this.projectWorldWriter.deleteProjectWorld(projectId);
                this.projectBus.serverIssue(ServerIssueType.NotFound);
                throw error;
            } else if (error instanceof EncryptedProjectError) {
                log.error('Client does not have symmetric key to decrypt events', projectId, error);
                this.projectBus.serverIssue(ServerIssueType.PendingDecryption);
            } else {
                log.error('Error reading events from server', projectId, error);
            }
        }
    }

    writeToServer = async (projectId: string) => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        try {
            await projectWorld.concurrentServerWriting(this.projectWorldReader.eventsNotOnServer, this.encryptEventsAndWriteToServer);
        } catch (error: unknown) {
            if (error instanceof ErrorForbidden) {
                log.error('Access forbidden to project.', projectId);
                this.projectBus.serverIssue(ServerIssueType.NoPermission);
            } else if (error instanceof ErrorNotExist) {
                log.error('Project not found on server', projectId);
                this.projectBus.serverIssue(ServerIssueType.NotFound);
            } else {
                log.error('Error writing events to server', projectId, error);
            }
            throw error;
        }
    }

    private readEventsFromServerAndDecrypt = async (projectId: string) => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        log.info('Attempting to read latest events from server', projectId);

        // Pull any new events from the server
        const shareKeyHash = neverEmptyString(this.navigationState.state.shareKey) ? await generateShortClientIdentity(this.navigationState.state.shareKey!) : undefined;
        const serverEventBatches = await this.serverCalls.readEvents(
            projectId,
            projectWorld.projectMetadata.serverEventsUpTo,
            shareKeyHash,
            false
        );

        // No symmetric key provided, search for it in the event stream
        let gainedSymmetricKey = false;
        if (projectWorld.projectMetadata.symmetricKey === undefined) {
            const publicKey = (await this.userIdentity.publicKey()).publicKeyStr;
            const processorProvidedSymmetricKey = projectWorld.eventProcessors.getProcessor(ProjectEventType.ProvidedSymmetricKey)!;
            const eventTypeId = processorProvidedSymmetricKey.eventType;

            const localEvent = {} as ProvidedSymmetricKey_LocalEvent;
            const encryptedSymmetricKeyEvent = serverEventBatches.flatMap(batch => batch.ev).find(event => {
                if (event.tp !== eventTypeId) return false;
                if (!processorProvidedSymmetricKey.fromServerEvent(localEvent, event)) return false;
                if (localEvent.publicKey !== publicKey) return false;
                return true;
            });
            if (encryptedSymmetricKeyEvent !== undefined) {
                const privateKeyString = await this.userIdentity.privateKeyString();

                // Import the private key for decryption
                const privateKey = await importRsaPrivateKeyForDecryption(privateKeyString);

                // Convert the encrypted symmetric key from base64 to ArrayBuffer
                const encryptedKeyBuffer = base64ToArrayBuffer(localEvent.encryptedSymmetricKey);

                // Decrypt the symmetric key
                const decryptedKeyBuffer = await decryptAsymmetric(privateKey, encryptedKeyBuffer);

                // Convert back to string
                projectWorld.projectMetadata.symmetricKey = ab2str(decryptedKeyBuffer);
                projectWorld.encryptedProject = await encryptedProject(projectWorld.projectMetadata.symmetricKey);
                gainedSymmetricKey = true;
            }
        }

        let localEvents: ProjectLocalEvent[] = [];
        try {
            localEvents = await this.decryptAndLoadServerEvents(projectWorld, serverEventBatches, true);
        } catch (e) {
            if (e instanceof EncryptedProjectError) {
                //Project syncing is not allowed to generate local events,
                //So just go realtime so we can wait for our symmetric key from creating client
                await this.maybeGoRealtime(projectWorld);
            }
            throw e;
        }
        await this.maybeGoRealtime(projectWorld);

        if (gainedSymmetricKey) {
            this.projectBus.projectLoaded(projectId);
            this.projectBus.serverIssue(ServerIssueType.Resolved);
        }


        return localEvents;
    }

    private encryptEventsAndWriteToServer = async (projectId: string, enforce_aggregate_concurrency: boolean, serverEvents: ServerEvent<ProjectEventType>[]) => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);

        if (projectWorld.encryptedProject !== undefined) {
            for (const serverEvent of serverEvents) {
                if (!projectWorld.eventProcessors.getProcessor(serverEvent.tp)?.shouldEncrypt) continue;
                const encryptionResult = await projectWorld.encryptedProject.encryptString(serverEvent.ev);
                serverEvent.ev = encryptionResult.output;
                serverEvent.iv = encryptionResult.iv;
            }
        }

        const writeResponse = await this.serverCalls.writeEvents(
            projectWorld.projectMetadata.id,
            !projectWorld.isExistOnServer,
            enforce_aggregate_concurrency ? projectWorld.projectMetadata.serverEventsUpTo : undefined,
            serverEvents
        );

        if (enforce_aggregate_concurrency) {
            // Read was up to date, so we can update the serverEventsUpTo
            projectWorld.projectMetadata.serverEventsUpTo = writeResponse.event_batch_index;
        }

        await this.projectWorldWriter.saveProjectWorldMetadata(projectWorld.projectMetadata);

        await this.maybeGoRealtime(projectWorld);
    }
}